import React from 'react';
import { FileText, Mail, FileCheck, BarChart2, ExternalLink } from 'lucide-react';
import { Draft, NiwtAgent } from '../types';
import { emitDraftOpen } from '../events';

const draftTypeIcons: Record<Draft['type'], React.FC<{ className?: string }>> = {
  email: Mail,
  proposal: FileCheck,
  memo: FileText,
  report: BarChart2,
};

const draftTypeLabels: Record<Draft['type'], string> = {
  email: 'Email',
  proposal: 'Proposal',
  memo: 'Memo',
  report: 'Report',
};

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

interface Props {
  drafts: Draft[];
  agents?: NiwtAgent[];
}

const DraftsReadyPanel: React.FC<Props> = ({ drafts, agents = [] }) => {
  const agentMap = Object.fromEntries(agents.map((a) => [a.id, a]));

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
          <FileText className="w-4 h-4 text-niwt-primary" />
          Drafts Ready ({drafts.length})
        </h2>
      </div>

      {drafts.length === 0 ? (
        <p className="text-xs text-gray-400 py-3 text-center">No drafts ready for review.</p>
      ) : (
        <div className="space-y-2">
          {drafts.map((draft) => {
            const Icon = draftTypeIcons[draft.type];
            const agent = agentMap[draft.prepared_by_agent_id];
            return (
              <div
                key={draft.id}
                className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="mt-0.5 p-1.5 bg-purple-50 rounded-md flex-shrink-0">
                  <Icon className="w-3 h-3 text-niwt-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-800 truncate">{draft.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {draftTypeLabels[draft.type]} · {draft.prepared_for}
                    {agent ? ` · by ${agent.display_name}` : ''}
                    {' · '}{relativeTime(draft.created_at)}
                  </p>
                </div>
                <button
                  onClick={() => emitDraftOpen(draft.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-niwt-primary hover:text-niwt-secondary"
                  aria-label="Open draft"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DraftsReadyPanel;
